import React, { useState, useEffect } from 'react';
import { supabase } from './supabaseClient';

const fmtDate = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const T = {
  bg:        '#060d1e',
  surface:   '#0d1a2d',
  surfaceHi: '#132238',
  border:    '#1e3a5f',
  borderHi:  '#2d5a8e',
  text:      '#f0f6ff',
  textSub:   '#8ba5c8',
  textMute:  '#4a6a8a',
  emerald:   '#10b981',
  emeraldDim:'rgba(16,185,129,0.10)',
  blue:      '#3b82f6',
  blueDim:   'rgba(59,130,246,0.10)',
  amber:     '#f59e0b',
  red:       '#ef4444',
  purple:    '#a78bfa',
};

export default function AdComTracker({ session, plan }) {
  const [meetings, setMeetings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('upcoming');
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState(null);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const isMobile = windowWidth < 768;

  useEffect(() => {
    fetchMeetings();
    const onResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  async function fetchMeetings() {
    setLoading(true);
    const { data } = await supabase
      .from('adcom_calendar')
      .select('*')
      .order('meeting_date', { ascending: true });
    setMeetings(data || []);
    setLoading(false);
  }

  const today = new Date().toISOString().split('T')[0];

  const daysUntil = (dateStr) => {
    if (!dateStr) return null;
    const d = new Date(); d.setHours(0,0,0,0);
    return Math.ceil((new Date(dateStr) - d) / (1000*60*60*24));
  };

  const filtered = meetings.filter(m => {
    if (filter === 'upcoming') return m.meeting_date && m.meeting_date >= today;
    if (filter === 'past') return m.meeting_date && m.meeting_date < today;
    return true;
  }).filter(m => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      m.drug_name?.toLowerCase().includes(q) ||
      m.company?.toLowerCase().includes(q) ||
      m.committee?.toLowerCase().includes(q) ||
      m.indication?.toLowerCase().includes(q)
    );
  });

  const upcomingCount = meetings.filter(m => m.meeting_date && m.meeting_date >= today).length;
  const within30 = meetings.filter(m => {
    const d = daysUntil(m.meeting_date);
    return d !== null && d >= 0 && d <= 30;
  }).length;

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto' }}>

      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <h2 style={{ fontSize: isMobile ? 20 : 26, fontWeight: 800, color: T.text, margin: '0 0 6px', letterSpacing: '-0.5px' }}>
          FDA AdCom Tracker
        </h2>
        <p style={{ color: T.textSub, fontSize: isMobile ? 14 : 16, margin: 0, lineHeight: 1.6 }}>
          FDA Advisory Committee meetings — independent expert panels that vote on drug approvals before the FDA decides.{' '}
          {within30 > 0 && <span style={{ color: T.amber, fontWeight: 700 }}>{within30} meeting{within30 !== 1 ? 's' : ''} in the next 30 days.</span>}
        </p>
      </div>

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2,1fr)' : 'repeat(3,1fr)', gap: 10, marginBottom: 24 }}>
        {[
          { label: 'Upcoming Meetings', value: upcomingCount,       color: T.emerald },
          { label: 'Within 30 Days',    value: within30,            color: T.amber   },
          { label: 'Total Tracked',     value: meetings.length,     color: T.blue    },
        ].map((s, i) => (
          <div key={i} style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 12, padding: isMobile ? '14px 16px' : '18px 22px' }}>
            <div style={{ fontSize: 11, color: T.textMute, fontWeight: 700, marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.07em' }}>{s.label}</div>
            <div style={{ fontSize: isMobile ? 24 : 30, fontWeight: 800, color: s.color }}>{loading ? '—' : s.value}</div>
          </div>
        ))}
      </div>

      {/* What is an AdCom */}
      <div style={{ marginBottom: 20, padding: '14px 18px', background: T.surface, border: `1px solid ${T.border}`, borderRadius: 10 }}>
        <div style={{ fontSize: 14, color: T.textSub, lineHeight: 1.7 }}>
          <span style={{ color: T.emerald, fontWeight: 700 }}>What is an AdCom?</span>{' '}
          Advisory committees are panels of independent experts the FDA convenes to review drug applications and vote on whether evidence supports approval.
          A positive vote is a strong bullish signal — the FDA follows AdCom recommendations roughly 75% of the time.
          A negative vote often kills the stock. These meetings typically happen 1–6 weeks before the PDUFA date.
        </div>
      </div>

      {/* Search + filters */}
      <div style={{ marginBottom: 16 }}>
        <div style={{ position: 'relative', marginBottom: 10 }}>
          <span style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: T.textMute, fontSize: 15 }}>⌕</span>
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search drug, committee, company..."
            style={{ width: '100%', padding: '11px 14px 11px 38px', borderRadius: 8, border: `1px solid ${T.border}`, background: T.surface, color: T.text, fontSize: 15, outline: 'none', boxSizing: 'border-box', transition: 'border-color 0.15s' }}
            onFocus={e => e.target.style.borderColor = T.emerald}
            onBlur={e => e.target.style.borderColor = T.border}
          />
        </div>
        <div style={{ display: 'flex', gap: 6, borderBottom: `1px solid ${T.border}`, paddingBottom: 0 }}>
          {[
            { id: 'upcoming', label: `Upcoming (${upcomingCount})` },
            { id: 'all',      label: `All (${meetings.length})` },
            { id: 'past',     label: `Past (${meetings.filter(m => m.meeting_date && m.meeting_date < today).length})` },
          ].map(f => {
            const isActive = filter === f.id;
            return (
              <button key={f.id} onClick={() => setFilter(f.id)} style={{
                padding: isMobile ? '10px 14px 9px' : '11px 20px 10px',
                border: 'none', borderBottom: `2px solid ${isActive ? T.emerald : 'transparent'}`,
                background: 'transparent', color: isActive ? T.text : T.textSub,
                cursor: 'pointer', fontSize: isMobile ? 13 : 15,
                fontWeight: isActive ? 700 : 400,
                transition: 'all 0.15s', marginBottom: '-1px', whiteSpace: 'nowrap',
              }}>{f.label}</button>
            );
          })}
        </div>
      </div>

      <div style={{ fontSize: 14, color: T.textMute, marginBottom: 14 }}>
        {filtered.length} {filter === 'upcoming' ? 'upcoming' : filter === 'past' ? 'past' : ''} meeting{filtered.length !== 1 ? 's' : ''} · FDA Advisory Committee Calendar
      </div>

      {/* Meeting cards */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: 60, color: T.textMute }}>
          <div style={{ fontSize: 15, marginBottom: 12 }}>Loading AdCom meetings...</div>
          <div style={{ width: 80, height: 2, background: T.border, borderRadius: 1, overflow: 'hidden', margin: '0 auto' }}>
            <div style={{ height: '100%', width: '50%', background: T.emerald, animation: 'loadBar 1.4s ease-in-out infinite' }} />
          </div>
        </div>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 60, color: T.textMute }}>
          <div style={{ fontSize: 32, marginBottom: 12 }}>—</div>
          <div style={{ fontSize: 16, marginBottom: 8 }}>No meetings found</div>
          <div style={{ fontSize: 14 }}>Try changing the filter or search term.</div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {filtered.map((meeting, i) => {
            const days = daysUntil(meeting.meeting_date);
            const isPast = days !== null && days < 0;
            const isUrgent = days !== null && days >= 0 && days <= 14;
            const accentColor = isPast ? T.textMute : isUrgent ? T.amber : T.emerald;
            const isExp = expanded === (meeting.id || i);

            return (
              <div key={meeting.id || i} style={{
                background: T.surface,
                border: `1px solid ${T.border}`,
                borderLeft: `3px solid ${accentColor}`,
                borderRadius: 12,
                overflow: 'hidden',
                transition: 'border-color 0.15s',
              }}
                onMouseEnter={e => e.currentTarget.style.borderColor = T.borderHi}
                onMouseLeave={e => e.currentTarget.style.borderColor = T.border}
              >
                <div style={{ padding: isMobile ? '14px 16px' : '18px 22px', cursor: 'pointer', display: 'flex', alignItems: 'flex-start', gap: 16 }}
                  onClick={() => setExpanded(isExp ? null : (meeting.id || i))}>

                  {/* Main info */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
                      <h3 style={{ margin: 0, fontSize: isMobile ? 15 : 17, fontWeight: 700, color: T.text }}>
                        {meeting.drug_name || 'Unnamed Drug'}
                      </h3>
                      <span style={{ fontSize: 11, fontWeight: 700, color: T.blue, background: T.blueDim, padding: '2px 8px', borderRadius: 4, flexShrink: 0 }}>
                        {meeting.meeting_type || 'AdCom'}
                      </span>
                      {meeting.application_type && (
                        <span style={{ fontSize: 11, fontWeight: 700, color: T.purple, background: 'rgba(167,139,250,0.1)', padding: '2px 8px', borderRadius: 4, flexShrink: 0 }}>
                          {meeting.application_type}
                        </span>
                      )}
                    </div>
                    <div style={{ fontSize: 14, color: T.emerald, fontWeight: 500, marginBottom: 4 }}>
                      {meeting.company && meeting.company !== 'Unknown' ? meeting.company : ''}
                    </div>
                    {meeting.committee && (
                      <div style={{ fontSize: 13, color: T.textSub, marginBottom: 4 }}>{meeting.committee}</div>
                    )}
                    {meeting.indication && (
                      <div style={{ fontSize: 14, color: T.textSub, lineHeight: 1.5 }}>
                        {meeting.indication.length > (isMobile ? 80 : 120) ? meeting.indication.substring(0, isMobile ? 80 : 120) + '...' : meeting.indication}
                      </div>
                    )}
                  </div>

                  {/* Countdown */}
                  <div style={{ flexShrink: 0, textAlign: 'right', minWidth: 70 }}>
                    {meeting.meeting_date && (
                      <>
                        <div style={{ background: `${accentColor}15`, border: `1px solid ${accentColor}30`, borderRadius: 8, padding: isMobile ? '7px 10px' : '10px 14px', marginBottom: 5, display: 'inline-block' }}>
                          <div style={{ fontSize: isMobile ? 16 : 20, fontWeight: 800, color: accentColor, lineHeight: 1 }}>
                            {isPast ? 'Past' : days === 0 ? 'Today' : `${days}d`}
                          </div>
                          {!isPast && <div style={{ fontSize: 10, color: accentColor, opacity: 0.8, fontWeight: 700, letterSpacing: '0.5px', marginTop: 3 }}>DAYS</div>}
                        </div>
                        <div style={{ fontSize: 12, color: T.textSub }}>{fmtDate(meeting.meeting_date)}</div>
                      </>
                    )}
                    <div style={{ fontSize: 12, color: T.textMute, marginTop: 4 }}>{isExp ? '▴ Less' : '▾ More'}</div>
                  </div>
                </div>

                {/* Expanded AI summary */}
                {isExp && (
                  <div style={{ borderTop: `1px solid ${T.border}`, background: T.surfaceHi, padding: isMobile ? '14px 16px' : '18px 22px' }}>
                    {meeting.ai_summary ? (
                      <div>
                        <div style={{ fontSize: 11, fontWeight: 700, color: T.textMute, letterSpacing: 1, marginBottom: 10, textTransform: 'uppercase' }}>AI Analysis</div>
                        <p style={{ margin: 0, fontSize: 15, color: T.textSub, lineHeight: 1.75 }}>{meeting.ai_summary}</p>
                      </div>
                    ) : (
                      <div style={{ fontSize: 14, color: T.textMute, fontStyle: 'italic' }}>No AI summary available for this meeting yet.</div>
                    )}
                    {meeting.fda_url && (
                      <a href={meeting.fda_url} target="_blank" rel="noopener noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 14, fontSize: 14, fontWeight: 700, color: T.emerald, textDecoration: 'none', padding: '8px 16px', border: `1px solid rgba(16,185,129,0.25)`, borderRadius: 7, background: T.emeraldDim, transition: 'all 0.15s' }}
                        onMouseEnter={e => e.currentTarget.style.background = 'rgba(16,185,129,0.18)'}
                        onMouseLeave={e => e.currentTarget.style.background = T.emeraldDim}>
                        View FDA Meeting Page →
                      </a>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Disclaimer */}
      <div style={{ marginTop: 24, padding: '14px 18px', background: T.surface, border: `1px solid ${T.border}`, borderRadius: 10 }}>
        <div style={{ fontSize: 14, color: T.textSub, lineHeight: 1.7 }}>
          Source: FDA Advisory Committee Calendar. Meetings updated as announced.
          AdCom votes are non-binding — the FDA typically follows recommendations but is not legally required to.
          Not financial advice.
        </div>
      </div>

      <style>{`@keyframes loadBar { 0% { transform: translateX(-100%); } 100% { transform: translateX(300%); } }`}</style>
    </div>
  );
}